import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  FileExcelOutlined,
  PlusOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from "antd";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import Footer from "../../../components/Footer";
import NavBar from "../../../components/NavBar";
import SideBar from "../../../components/SideBar";
import axiosInstance from "../../../variables/api/axiosInstance";
import ActionDropdown from "../../production/components/ActionDropdown";

const { Text, Title } = Typography;

const normalize = (value) => (value || "").trim().replace(/\s+/g, " ").toUpperCase();

const Marques = () => {
  const [marques, setMarques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [marqueModalOpen, setMarqueModalOpen] = useState(false);
  const [modeleModalOpen, setModeleModalOpen] = useState(false);
  const [selectedMarque, setSelectedMarque] = useState(null);
  const [selectedModele, setSelectedModele] = useState(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState(null);
  const [marqueForm] = Form.useForm();
  const [modeleForm] = Form.useForm();

  const sortedMarques = useMemo(
    () => [...marques].sort((a, b) => (a.nom || "").localeCompare(b.nom || "", "fr", { sensitivity: "base" })),
    [marques]
  );

  const fetchMarques = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/vehicule-marques");
      setMarques(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Erreur lors de la récupération des marques.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMarques();
  }, []);

  const openMarqueModal = (marque = null) => {
    setSelectedMarque(marque);
    marqueForm.setFieldsValue({ nom: marque?.nom || "" });
    setMarqueModalOpen(true);
  };

  const openModeleModal = (marque, modele = null) => {
    setSelectedMarque(marque);
    setSelectedModele(modele);
    modeleForm.setFieldsValue({ nom: modele?.nom || "" });
    setModeleModalOpen(true);
  };

  const saveMarque = async () => {
    const values = await marqueForm.validateFields();
    const nom = normalize(values.nom);
    const duplicate = marques.some(
      (m) => normalize(m.nom) === nom && m.marqueId !== selectedMarque?.marqueId
    );
    if (duplicate) {
      toast.warn("Cette marque existe déjà.");
      return;
    }
    try {
      if (selectedMarque?.marqueId) {
        await axiosInstance.put(`/api/vehicule-marques/${selectedMarque.marqueId}`, { nom });
        toast.success("Marque modifiée avec succès.");
      } else {
        await axiosInstance.post("/api/vehicule-marques", { nom });
        toast.success("Marque ajoutée avec succès.");
      }
      setMarqueModalOpen(false);
      setSelectedMarque(null);
      marqueForm.resetFields();
      fetchMarques();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement de la marque.");
    }
  };

  const saveModele = async () => {
    const values = await modeleForm.validateFields();
    const nom = normalize(values.nom);
    const modeles = selectedMarque?.modeles || [];
    const duplicate = modeles.some(
      (m) => normalize(m.nom) === nom && m.modeleId !== selectedModele?.modeleId
    );
    if (duplicate) {
      toast.warn("Ce modèle existe déjà pour cette marque.");
      return;
    }
    try {
      if (selectedModele?.modeleId) {
        await axiosInstance.put(`/api/vehicule-marques/modeles/${selectedModele.modeleId}`, { nom });
        toast.success("Modèle modifié avec succès.");
      } else {
        await axiosInstance.post(`/api/vehicule-marques/${selectedMarque.marqueId}/modeles`, { nom });
        toast.success("Modèle ajouté avec succès.");
      }
      setModeleModalOpen(false);
      setSelectedModele(null);
      modeleForm.resetFields();
      fetchMarques();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'enregistrement du modèle.");
    }
  };

  const confirmDeleteMarque = (marque) => {
    Modal.confirm({
      title: "Supprimer cette marque ?",
      icon: <ExclamationCircleOutlined />,
      content: "La marque ne sera plus proposée dans les nouveaux formulaires.",
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/api/vehicule-marques/${marque.marqueId}`);
          toast.success("Marque supprimée.");
          fetchMarques();
        } catch (err) {
          console.error(err);
          toast.error("Erreur lors de la suppression.");
        }
      },
    });
  };

  const confirmDeleteModele = (modele) => {
    Modal.confirm({
      title: "Supprimer ce modèle ?",
      icon: <ExclamationCircleOutlined />,
      okText: "Supprimer",
      okType: "danger",
      cancelText: "Annuler",
      onOk: async () => {
        try {
          await axiosInstance.delete(`/api/vehicule-marques/modeles/${modele.modeleId}`);
          toast.success("Modèle supprimé.");
          fetchMarques();
        } catch (err) {
          console.error(err);
          toast.error("Erreur lors de la suppression.");
        }
      },
    });
  };

  const importExcel = async () => {
    if (!importFile) {
      toast.warn("Sélectionnez un fichier Excel.");
      return;
    }
    const formData = new FormData();
    formData.append("file", importFile);
    setImporting(true);
    try {
      const res = await axiosInstance.post("/api/vehicule-marques/import-excel", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportReport(res.data);
      toast.success("Import terminé.");
      fetchMarques();
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'import Excel.");
    } finally {
      setImporting(false);
    }
  };

  const modeleColumns = (marque) => [
    {
      title: "Modèle / Type",
      dataIndex: "nom",
      key: "nom",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <ActionDropdown
          actions={[
            {
              key: "edit",
              label: "Modifier",
              icon: <EditOutlined />,
              onClick: () => openModeleModal(marque, record),
            },
            {
              key: "delete",
              label: "Supprimer",
              icon: <DeleteOutlined />,
              danger: true,
              onClick: () => confirmDeleteModele(record),
            },
          ]}
        />
      ),
    },
  ];

  const columns = [
    {
      title: "Marque",
      dataIndex: "nom",
      key: "nom",
      render: (v, record) => (
        <Space>
          <Text strong>{v}</Text>
          <Tag color="blue">{record.modeles?.length || 0} modèle{(record.modeles?.length || 0) > 1 ? "s" : ""}</Tag>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 180,
      render: (_, record) => (
        <Space>
          <Button size="small" icon={<PlusOutlined />} onClick={() => openModeleModal(record)}>
            Modèle
          </Button>
          <ActionDropdown
            actions={[
              {
                key: "edit",
                label: "Modifier",
                icon: <EditOutlined />,
                onClick: () => openMarqueModal(record),
              },
              {
                key: "delete",
                label: "Supprimer",
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => confirmDeleteMarque(record),
              },
            ]}
          />
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-wrapper layout-content-navbar">
      <div className="layout-container">
        <SideBar currentMenu="TARIFICATION" />
        <div className="layout-page">
          <NavBar />
          <div className="content-wrapper">
            <div className="container-xxl flex-grow-1 container-p-y">
              <Row align="middle" justify="space-between" style={{ marginBottom: 16 }}>
                <Col>
                  <Title level={4} style={{ margin: 0 }}>Marques et modèles de véhicules</Title>
                  <Text type="secondary">Gestion globale, sans filtre par catégorie</Text>
                </Col>
                <Col>
                  <Space>
                    <Button icon={<FileExcelOutlined />} onClick={() => setImportModalOpen(true)}>
                      Importer Excel
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openMarqueModal()}>
                      Ajouter une marque
                    </Button>
                  </Space>
                </Col>
              </Row>

              <Card bodyStyle={{ padding: 0 }}>
                <Table
                  columns={columns}
                  dataSource={sortedMarques}
                  rowKey="marqueId"
                  loading={loading}
                  expandable={{
                    expandedRowRender: (record) => (
                      <Table
                        columns={modeleColumns(record)}
                        dataSource={record.modeles || []}
                        rowKey="modeleId"
                        pagination={false}
                        locale={{ emptyText: "Aucun modèle paramétré pour cette marque." }}
                        size="small"
                      />
                    ),
                  }}
                  pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ["10", "20", "50"],
                    showTotal: (total) => `${total} marque${total > 1 ? "s" : ""}`,
                  }}
                />
              </Card>
            </div>
            <Footer />
          </div>
        </div>
      </div>

      <Modal
        title={selectedMarque ? "Modifier la marque" : "Ajouter une marque"}
        open={marqueModalOpen}
        onOk={saveMarque}
        onCancel={() => setMarqueModalOpen(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={marqueForm} layout="vertical">
          <Form.Item name="nom" label="Nom" rules={[{ required: true, message: "Le nom est requis" }]}>
            <Input placeholder="Ex. TOYOTA" onChange={(e) => marqueForm.setFieldValue("nom", normalize(e.target.value))} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={selectedModele ? "Modifier le modèle" : `Ajouter un modèle${selectedMarque?.nom ? ` - ${selectedMarque.nom}` : ""}`}
        open={modeleModalOpen}
        onOk={saveModele}
        onCancel={() => setModeleModalOpen(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        <Form form={modeleForm} layout="vertical">
          <Form.Item name="nom" label="Modèle / Type" rules={[{ required: true, message: "Le modèle est requis" }]}>
            <Input placeholder="Ex. COROLLA" onChange={(e) => modeleForm.setFieldValue("nom", normalize(e.target.value))} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Importer des marques et modèles"
        open={importModalOpen}
        onOk={importExcel}
        onCancel={() => {
          setImportModalOpen(false);
          setImportFile(null);
          setImportReport(null);
        }}
        okText="Importer"
        cancelText="Fermer"
        confirmLoading={importing}
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="Format attendu"
          description="Première feuille Excel avec les colonnes Marque et Modele ou Type. Les doublons du fichier et les doublons déjà en base sont ignorés."
        />
        <Upload
          accept=".xlsx,.xls"
          maxCount={1}
          beforeUpload={(file) => {
            setImportFile(file);
            setImportReport(null);
            return false;
          }}
          onRemove={() => setImportFile(null)}
        >
          <Button icon={<UploadOutlined />}>Choisir un fichier Excel</Button>
        </Upload>
        {importReport && (
          <Card size="small" style={{ marginTop: 16 }}>
            <Space direction="vertical" size={4}>
              <Text>Lignes lues : <strong>{importReport.totalRows}</strong></Text>
              <Text>Marques créées : <strong>{importReport.createdMarques}</strong></Text>
              <Text>Marques déjà existantes : <strong>{importReport.existingMarques}</strong></Text>
              <Text>Modèles créés : <strong>{importReport.createdModeles}</strong></Text>
              <Text>Modèles déjà existants : <strong>{importReport.existingModeles}</strong></Text>
              <Text>Doublons dans le fichier : <strong>{importReport.duplicateRows}</strong></Text>
              <Text>Lignes ignorées : <strong>{importReport.ignoredRows}</strong></Text>
              {(importReport.errors || []).map((err, idx) => (
                <Text type="danger" key={idx}>{err}</Text>
              ))}
            </Space>
          </Card>
        )}
      </Modal>
    </div>
  );
};

export default Marques;
